import type {
  Channel,
  NewTask,
  Project,
  Task,
  User,
  PersonalItem,
  NewPersonalItem,
  PersonalItemPatch,
  TaskPatch,
  RecurrenceConfig,
} from './types';
import { localDateTime, parseDuration, resolveSchedule, validatedTimeZone } from './datetime';
import { occurrenceAt } from './recurrence';
import type { AiScope } from './ai';

export interface CommandContext {
  tasks: readonly Task[];
  users: readonly User[];
  channels: readonly Channel[];
  projects: readonly Project[];
  items?: readonly PersonalItem[];
  timeZone?: string;
  now?: Date;
}
export type CommandAction =
  | { type: 'help' }
  | { type: 'clear' }
  | { type: 'navigate'; path: string }
  | { type: 'task.list' }
  | { type: 'task.new' }
  | { type: 'task.create'; payload: NewTask }
  | { type: 'task.update'; id: string; patch: TaskPatch }
  | { type: 'task.delete'; id: string }
  | { type: 'project.delete'; id: string }
  | { type: 'item.create'; payload: NewPersonalItem }
  | { type: 'item.update'; id: string; patch: PersonalItemPatch }
  | {
      type: 'recurrence.update';
      kind: 'task' | 'reminder';
      id: string;
      expectedVersion: number;
      recurrence: null;
    }
  | { type: 'agenda.status' }
  | { type: 'ai.ask'; prompt: string; scope: AiScope }
  | {
      type: 'atomic.command';
      command: 'status' | 'calendar' | 'approvals' | 'content' | 'analytics' | 'ads' | 'decisions';
      days?: number;
    }
  | { type: 'atomic.draft'; mode: 'post' | 'brief'; text: string }
  | { type: 'search'; query: string }
  | { type: 'message.send'; payload: { body: string; channelId?: string; recipientId?: string } }
  | { type: 'channel.create'; payload: { name: string; description?: string } };

export interface Completion {
  input: string;
  label: string;
  detail: string;
}
export class CommandError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CommandError';
  }
}

export const COMMANDS = [
  {
    usage: '/atomic <status|calendar|approvals|content|analytics|ads|decisions>',
    description:
      'Read bounded data from the configured Atomic workspace. Requires Atomic read permission.',
    example: '/atomic approvals',
    category: 'Atomic',
  },
  {
    usage: '/atomic calendar [1–31 days]',
    description: 'Read upcoming Atomic calendar entries. This does not schedule or publish.',
    example: '/atomic calendar 14',
    category: 'Atomic',
  },
  {
    usage: '/cmd post <text>',
    description:
      'Create an exact-text Atomic content draft for Atomic review. Requires Atomic draft permission; nothing is published.',
    example: '/cmd post Questions to consider for the next update?',
    category: 'Atomic',
  },
  {
    usage: '/cmd brief <prompt>',
    description:
      'Queue one Luna-generated Atomic draft through the existing budgeted workflow. Decisions are advisory; Atomic approval is still required.',
    example: '/cmd brief Draft a short update about our new guide',
    category: 'Atomic',
  },
  {
    usage: '/ask [--shared] <question>',
    description:
      'Ask about your work and private items. --shared limits context to shared tasks and projects. Actions require review.',
    example: '/ask What should I work on next?',
    category: 'Assistant',
  },
  {
    usage: '/status --ai',
    description:
      'Ask for an AI summary of your own work and private items. Plain /status remains deterministic.',
    example: '/status --ai',
    category: 'Assistant',
  },
  {
    usage:
      '/repeat task <title> --every daily|weekly|monthly --from YYYY-MM-DD [--tz <zone>] [--project <id>]',
    description: 'Repeat a shared task. Completion keeps its history and creates one next task.',
    example: '/repeat task "Weekly review" --every weekly --from 2026-10-19',
    category: 'Daily workflow',
  },
  {
    usage:
      '/repeat reminder <title> --every daily|weekly|monthly --at "tomorrow 10:00" [--tz <zone>] [--project <id>]',
    description: 'Repeat a private reminder at a local calendar time. Alerts are in-app.',
    example: '/repeat reminder "Check expenses" --every monthly --at "2026-11-01 09:00"',
    category: 'Daily workflow',
  },
  {
    usage: '/repeat stop task|reminder <id-or-unique-prefix>',
    description: 'Stop future repeats while keeping the current occurrence and completed history.',
    example: '/repeat stop reminder ',
    category: 'Daily workflow',
  },
  {
    usage: '/today',
    description: 'Your overdue and upcoming work, reminders and waiting-on items.',
    example: '/today',
    category: 'Daily workflow',
  },
  {
    usage: '/morning',
    description: 'Open your daily agenda. The same live view as /today.',
    example: '/morning',
    category: 'Daily workflow',
  },
  {
    usage: '/upcoming [7|30] [--project <id-or-unique-prefix>]',
    description: 'See scheduled work over 7 or 30 days: shared tasks and your private items.',
    example: '/upcoming 7',
    category: 'Daily workflow',
  },
  {
    usage: '/notifications',
    description: 'Open Settings to opt in to background alerts and the morning digest.',
    example: '/notifications',
    category: 'Daily workflow',
  },
  {
    usage: '/profile [@person]',
    description: 'Edit your profile or view a teammate’s profile.',
    example: '/profile',
    category: 'Workspace',
  },
  {
    usage: '/admin',
    description: 'Open user management. Available to workspace administrators.',
    example: '/admin',
    category: 'Workspace',
  },
  {
    usage: '/crm',
    description: 'Open the freelancer CRM. Available to workspace administrators.',
    example: '/crm',
    category: 'Workspace',
  },
  {
    usage: '/forgotten [7|14|30] [--project <id-or-unique-prefix>]',
    description: 'Review your unfinished tasks with no changes for at least 7, 14 or 30 days.',
    example: '/forgotten 7',
    category: 'Daily workflow',
  },
  {
    usage: '/status',
    description: 'A private, factual snapshot of your daily work in Terminal.',
    example: '/status',
    category: 'Daily workflow',
  },
  {
    usage: '/remind <title> --in 30m | --at "tomorrow 10:00" [--tz Europe/London] [--project <id>]',
    description: 'Create a private in-app reminder. Times use the displayed timezone.',
    example: '/remind "Review launch checklist" --in 30m',
    category: 'Daily workflow',
  },
  {
    usage:
      '/waiting [<title> --on <person> [--at "YYYY-MM-DD HH:mm"] [--tz <zone>] [--project <id>]]',
    description: 'Track what you are waiting for privately. No message is sent.',
    example: '/waiting "Design feedback" --on "Alex"',
    category: 'Daily workflow',
  },
  {
    usage:
      '/followup [<title> --in 2h | --at "tomorrow 10:00" [--on <person>] [--task <id> | --chat @person|#channel] [--project <id>] [--tz <zone>]]',
    description: 'Schedule a private chase linked to a task or conversation. No message is sent.',
    example: '/followup "Chase feedback" --in 2h --chat #general',
    category: 'Daily workflow',
  },
  {
    usage: '/resolve <waiting-id-or-unique-prefix>',
    description: 'Resolve a private waiting item or follow-up.',
    example: '/resolve ',
    category: 'Daily workflow',
  },
  {
    usage: '/reminders',
    description: 'View and manage your private reminders.',
    example: '/reminders',
    category: 'Daily workflow',
  },
  {
    usage: '/snooze <id-or-unique-prefix> <30m|2h|1d>',
    description: 'Move an open reminder or follow-up forward by an elapsed interval.',
    example: '/snooze ',
    category: 'Daily workflow',
  },
  {
    usage: '/complete <id-or-unique-prefix>',
    description: 'Complete one of your reminders or waiting-on items.',
    example: '/complete ',
    category: 'Daily workflow',
  },
  {
    usage: '/note <text> [--project <id>]',
    description: 'Capture a private note without creating a task.',
    example: '/note "Idea for the next launch"',
    category: 'Notes and search',
  },
  {
    usage: '/notes',
    description: 'Open your private notes.',
    example: '/notes',
    category: 'Notes and search',
  },
  {
    usage: '/bookmark <https://url> <title> [--project <id>]',
    description: 'Save a private link, optionally linked to a project.',
    example: '/bookmark https://render.com/docs "Render documentation"',
    category: 'Notes and search',
  },
  {
    usage: '/bookmarks',
    description: 'Open your saved links.',
    example: '/bookmarks',
    category: 'Notes and search',
  },
  {
    usage: '/find [text]',
    description: 'Search workspace tasks and projects plus your private items.',
    example: '/find launch',
    category: 'Notes and search',
  },
  {
    usage: '/new-project',
    description: 'Set up a project, then open its Kanban board.',
    example: '/new-project',
    category: 'Projects',
  },
  {
    usage: '/project <id-or-unique-prefix> [--context]',
    description: 'Open a project board, optionally showing your private project context.',
    example: '/project ',
    category: 'Projects',
  },
  {
    usage: '/delete-project <id-or-unique-prefix>',
    description: 'Delete a project after confirmation. Its tasks stay in the workspace.',
    example: '/delete-project ',
    category: 'Projects',
  },
  {
    usage: 'help',
    description: 'Show commands and examples.',
    example: 'help',
    category: 'Terminal',
  },
  {
    usage: 'clear',
    description: 'Clear the terminal transcript.',
    example: 'clear',
    category: 'Terminal',
  },
  {
    usage: 'c [title] [--priority low|normal|high] [--due YYYY-MM-DD] [--assign @person]',
    description:
      'Quick create. c alone opens the Create task window; c <title> creates the task straight away.',
    example: 'c Call Acme about the renewal --due 2026-11-03',
    category: 'Tasks',
  },
  {
    usage: 'task add <title> [--priority low|normal|high] [--due YYYY-MM-DD] [--assign @person]',
    description: 'Create a task. Quote a title to preserve spacing or flag-like words.',
    example: 'task add "Review launch plan" --priority high',
    category: 'Tasks',
  },
  {
    usage: 'task list',
    description: 'Show workspace tasks.',
    example: 'task list',
    category: 'Tasks',
  },
  {
    usage: 'task done <id-or-unique-prefix>',
    description: 'Complete a task.',
    example: 'task done a1b2',
    category: 'Tasks',
  },
  {
    usage: 'task start <id-or-unique-prefix>',
    description: 'Mark a task in progress.',
    example: 'task start a1b2',
    category: 'Tasks',
  },
  {
    usage: 'task reopen <id-or-unique-prefix>',
    description: 'Return a task to To do.',
    example: 'task reopen a1b2',
    category: 'Tasks',
  },
  {
    usage: 'task delete <id-or-unique-prefix>',
    description: 'Request task deletion with confirmation.',
    example: 'task delete a1b2',
    category: 'Tasks',
  },
  {
    usage: 'msg @person <message>',
    description:
      'Send a private message. Use an email or a unique name; quote names containing spaces.',
    example: 'msg @alex@example.com Ready to review?',
    category: 'Messages',
  },
  {
    usage: 'msg #channel <message>',
    description: 'Send a message to a workspace channel.',
    example: 'msg #general Launch checklist is ready.',
    category: 'Messages',
  },
  {
    usage: 'open tasks|projects|terminal|shortcuts|settings|#channel|@person',
    description: 'Navigate to a page or conversation.',
    example: 'open #general',
    category: 'Navigation',
  },
  {
    usage: 'channel add <slug> [description]',
    description: 'Create a channel using lowercase letters, numbers and hyphens.',
    example: 'channel add launches Product launch coordination',
    category: 'Channels',
  },
] as const;

interface Token {
  value: string;
  start: number;
  end: number;
  quoted: boolean;
  closed: boolean;
}

// Quoting is syntax only at the start of a token. Apostrophes in don't or O'Neil
// remain ordinary text; no token is ever interpreted by a shell.
function tokenize(source: string, incomplete = false, limit = Infinity): Token[] {
  const result: Token[] = [];
  let i = 0;
  while (i < source.length && result.length < limit) {
    while (/\s/.test(source[i] ?? '') && i < source.length) i++;
    if (i >= source.length) break;
    const start = i;
    let value = '';
    const quote = source[i] === '"' || source[i] === "'" ? source[i++] : null;
    let closed = !quote;
    if (quote) {
      while (i < source.length) {
        if (source[i] === quote) {
          i++;
          closed = true;
          break;
        }
        if (source[i] === '\\' && (source[i + 1] === quote || source[i + 1] === '\\')) i++;
        value += source[i++];
      }
      if (!closed && !incomplete)
        throw new CommandError(`Close the ${quote} quote before running this command.`);
      if (closed && i < source.length && !/\s/.test(source[i]))
        throw new CommandError('Add a space after the closing quote.');
    } else {
      while (i < source.length && !/\s/.test(source[i])) value += source[i++];
    }
    result.push({ value, start, end: i, quoted: Boolean(quote), closed });
  }
  return result;
}

function fail(message: string): never {
  throw new CommandError(message);
}
function exactArgs(tokens: Token[], length: number, usage: string) {
  if (tokens.length !== length) fail(`Use: ${usage}`);
}
function resolvePerson(value: string, context: CommandContext): User {
  if (!value.startsWith('@') || value.length === 1)
    return fail('Choose a person using @email or a unique @name. Quote names containing spaces.');
  const query = value.slice(1).toLowerCase();
  const byEmail = context.users.filter((user) => user.email.toLowerCase() === query);
  const matches = byEmail.length
    ? byEmail
    : context.users.filter((user) => user.name.toLowerCase() === query);
  if (!matches.length)
    return fail(`No workspace person matches ${value}. Use a full email or unique name.`);
  if (matches.length > 1)
    return fail(`More than one person matches ${value}. Use their full email address.`);
  return matches[0];
}
function resolveChannel(value: string, context: CommandContext): Channel {
  const matches = context.channels.filter(
    (channel) => `#${channel.name}`.toLowerCase() === value.toLowerCase(),
  );
  if (matches.length !== 1)
    return fail(`No unique workspace channel matches ${value}. Use an existing #channel name.`);
  return matches[0];
}
function resolveTask(value: string, context: CommandContext): Task {
  const exact = context.tasks.find((task) => task.id.toLowerCase() === value.toLowerCase());
  if (exact) return exact;
  const matches = context.tasks.filter((task) =>
    task.id.toLowerCase().startsWith(value.toLowerCase()),
  );
  if (!matches.length)
    return fail(`No task ID starts with "${value}". Run task list to find its ID.`);
  if (matches.length > 1)
    return fail(`Task prefix "${value}" matches ${matches.length} tasks. Enter more of the ID.`);
  return matches[0];
}
function resolveProject(value: string, context: CommandContext): Project {
  const query = value.toLowerCase();
  const exact = context.projects.find((project) => project.id.toLowerCase() === query);
  if (exact) return exact;
  const matches = context.projects.filter((project) => project.id.toLowerCase().startsWith(query));
  if (!matches.length)
    return fail(`No project ID starts with "${value}". Choose a project from the suggestions.`);
  if (matches.length > 1)
    return fail(
      `Project prefix "${value}" matches ${matches.length} projects. Enter more of the ID.`,
    );
  return matches[0];
}
function resolveItem(value: string, context: CommandContext): PersonalItem {
  const matches = (context.items || []).filter((item) =>
    item.id.toLowerCase().startsWith(value.toLowerCase()),
  );
  if (!matches.length)
    return fail(
      `No personal item ID starts with "${value}". Open /reminders or /waiting to find it.`,
    );
  if (matches.length > 1)
    return fail('That prefix matches several personal items. Enter more of the ID.');
  return matches[0];
}

function personalCommand(tokens: Token[], context: CommandContext): CommandAction {
  const verb = tokens[0].value.toLowerCase();
  const values: string[] = [];
  const flags: Record<string, string> = {};
  const allowed =
    verb === 'remind'
      ? ['--in', '--at', '--tz', '--project']
      : verb === 'followup'
        ? ['--on', '--in', '--at', '--tz', '--project', '--task', '--chat']
        : verb === 'waiting'
          ? ['--on', '--in', '--at', '--tz', '--project']
          : ['--project'];
  let literal = false;
  for (let i = 1; i < tokens.length; i++) {
    const token = tokens[i];
    if (!literal && !token.quoted && token.value === '--') {
      literal = true;
      continue;
    }
    if (!literal && !token.quoted && token.value.startsWith('--')) {
      if (!allowed.includes(token.value))
        return fail(`Unknown option ${token.value}. Use ${allowed.join(', ')}.`);
      if (Object.hasOwn(flags, token.value))
        return fail(`${token.value} can only be supplied once.`);
      const next = tokens[++i];
      if (!next || (!next.quoted && next.value.startsWith('--')))
        return fail(`Provide a value after ${token.value}.`);
      flags[token.value] = next.value;
    } else values.push(token.value);
  }
  const zone = validatedTimeZone(
    flags['--tz'] || context.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const payload: NewPersonalItem = {
    kind:
      verb === 'remind'
        ? 'reminder'
        : verb === 'followup'
          ? 'waiting'
          : (verb as 'waiting' | 'note' | 'bookmark'),
    title: '',
    timeZone: zone,
  };
  if (flags['--project']) payload.projectId = resolveProject(flags['--project'], context).id;
  if (verb === 'bookmark') {
    const raw = values.shift();
    let url: URL;
    try {
      url = new URL(raw || '');
    } catch {
      return fail('Use an absolute http:// or https:// bookmark URL.');
    }
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.username ||
      url.password ||
      url.href.length > 2048
    )
      return fail(
        'Use an http:// or https:// URL without embedded credentials, up to 2,048 characters.',
      );
    payload.url = url.href;
  }
  const content = boundedText(
    values.join(' '),
    verb === 'note' ? 'Note' : 'Title',
    verb === 'note' ? 10000 : 240,
  );
  payload.title = content.length > 240 ? `${content.slice(0, 237)}…` : content;
  if (verb === 'note') payload.body = content;
  if (verb === 'waiting' || (verb === 'followup' && flags['--on']))
    payload.counterparty = boundedText(
      flags['--on'] || '',
      'Person or organisation you are waiting on (--on)',
      160,
    );
  if (flags['--at'] && flags['--in']) return fail('Use either --at or --in, not both.');
  if (flags['--at']) payload.scheduledAt = resolveSchedule(flags['--at'], zone, context.now);
  if (flags['--in'])
    payload.scheduledAt = new Date(
      (context.now || new Date()).getTime() + parseDuration(flags['--in']),
    ).toISOString();
  if (verb === 'remind' && !payload.scheduledAt)
    return fail('Give your reminder a time: --in 30m or --at "tomorrow 10:00".');
  if (verb === 'followup') {
    if (!payload.scheduledAt)
      return fail('Give your follow-up a time: --in 2h or --at "tomorrow 10:00".');
    if (flags['--task'] && flags['--chat']) return fail('Choose one source: --task or --chat.');
    if (flags['--task'])
      payload.source = { kind: 'task', id: resolveTask(flags['--task'], context).id };
    if (flags['--chat']) {
      const target = flags['--chat'];
      if (target.startsWith('@'))
        payload.source = { kind: 'dm', id: resolvePerson(target, context).id };
      else if (target.startsWith('#'))
        payload.source = { kind: 'channel', id: resolveChannel(target, context).id };
      else return fail('Use --chat @person or --chat #channel.');
    }
  }
  return { type: 'item.create', payload };
}
function repeatCommand(tokens: Token[], context: CommandContext): CommandAction {
  const operation = tokens[1]?.value.toLowerCase();
  if (operation === 'stop') {
    exactArgs(tokens, 4, '/repeat stop task|reminder <id>');
    const kind = tokens[2].value.toLowerCase();
    const id = boundedText(tokens[3].value, 'Occurrence ID', 100);
    if (kind !== 'task' && kind !== 'reminder')
      return fail('Choose task or reminder after /repeat stop.');
    const resource = kind === 'task' ? resolveTask(id, context) : resolveItem(id, context);
    if (
      !resource.recurrence?.active ||
      ('kind' in resource && resource.kind !== 'reminder') ||
      ('status' in resource ? resource.status === 'done' : !!resource.completedAt)
    )
      return fail('Choose the current open occurrence of an active repeating task or reminder.');
    return {
      type: 'recurrence.update',
      kind,
      id: resource.id,
      expectedVersion: resource.version,
      recurrence: null,
    };
  }
  if (operation !== 'task' && operation !== 'reminder')
    return fail(
      'Use /repeat task <title> --every weekly --from YYYY-MM-DD, /repeat reminder <title> --every daily --at "tomorrow 10:00", or /repeat stop task|reminder <id>.',
    );
  const allowed =
    operation === 'task'
      ? ['--every', '--from', '--tz', '--project', '--priority', '--assign']
      : ['--every', '--at', '--tz', '--project'];
  const flags: Record<string, string> = {};
  const title: string[] = [];
  let literal = false;
  for (let i = 2; i < tokens.length; i++) {
    const token = tokens[i];
    if (!literal && !token.quoted && token.value === '--') {
      literal = true;
      continue;
    }
    if (!literal && !token.quoted && token.value.startsWith('--')) {
      if (!allowed.includes(token.value))
        return fail(`Unknown repeat option ${token.value}. Use ${allowed.join(', ')}.`);
      if (Object.hasOwn(flags, token.value))
        return fail(`${token.value} can only be supplied once.`);
      const next = tokens[++i];
      if (!next || (!next.quoted && next.value.startsWith('--')))
        return fail(`Provide a value after ${token.value}.`);
      flags[token.value] = next.value;
    } else title.push(token.value);
  }
  if (!['daily', 'weekly', 'monthly'].includes(flags['--every']))
    return fail('Choose --every daily, weekly or monthly.');
  const name = boundedText(title.join(' '), 'Title', 240);
  const timeZone = validatedTimeZone(
    flags['--tz'] || context.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const projectId = flags['--project'] ? resolveProject(flags['--project'], context).id : undefined;
  if (operation === 'reminder') {
    if (!flags['--at']) return fail('Choose the first reminder time with --at "tomorrow 10:00".');
    const scheduledAt = resolveSchedule(flags['--at'], timeZone, context.now);
    const local = localDateTime(new Date(scheduledAt), timeZone);
    return {
      type: 'item.create',
      payload: {
        kind: 'reminder',
        title: name,
        timeZone,
        scheduledAt,
        ...(projectId ? { projectId } : {}),
        recurrence: {
          frequency: flags['--every'] as RecurrenceConfig['frequency'],
          timeZone,
          anchorDate: local.slice(0, 10),
          localTime: local.slice(11),
        },
      },
    };
  }
  const recurrence: RecurrenceConfig = {
    frequency: flags['--every'] as RecurrenceConfig['frequency'],
    timeZone,
    anchorDate: flags['--from'] || '',
    localTime: null,
  };
  const first = occurrenceAt(recurrence, 0);
  if (flags['--priority'] && !['low', 'normal', 'high'].includes(flags['--priority']))
    return fail('Priority must be low, normal or high.');
  return {
    type: 'task.create',
    payload: {
      title: name,
      dueDate: first.dueDate,
      recurrence,
      ...(projectId ? { projectId } : {}),
      ...(flags['--priority'] ? { priority: flags['--priority'] as Task['priority'] } : {}),
      ...(flags['--assign'] ? { assigneeId: resolvePerson(flags['--assign'], context).id } : {}),
    },
  };
}

function validDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
function boundedText(value: string, label: string, maximum: number): string {
  const trimmed = value.trim();
  if (!trimmed) return fail(`${label} cannot be empty.`);
  if (trimmed.length > maximum)
    return fail(`${label} must be ${maximum.toLocaleString('en-GB')} characters or fewer.`);
  return trimmed;
}

export function parseCommand(input: string, context: CommandContext): CommandAction {
  const source = input.trim().replace(/^\/\s*/, '');
  if (!source) return fail('Enter a command. Try help or task add <title>.');
  const askHead = /^ask(?:\s|$)/i.exec(source);
  if (askHead) {
    let prompt = source.slice(askHead[0].length).trim();
    let scope: AiScope = 'personal';
    if (/^--shared(?:\s|$)/.test(prompt)) {
      scope = 'workspace';
      prompt = prompt.slice('--shared'.length).trim();
    }
    return { type: 'ai.ask', prompt: boundedText(prompt, 'Question', 2000), scope };
  }
  // Message bodies are opaque text. Quotes, whitespace, flags and Unicode in the
  // body are preserved and cannot become another command.
  const head = tokenize(source, false, 2);
  const verb = head[0]?.value.toLowerCase();
  if (verb === 'atomic') {
    const command = head[1]?.value.toLowerCase();
    const commands = [
      'status',
      'calendar',
      'approvals',
      'content',
      'analytics',
      'ads',
      'decisions',
    ] as const;
    if (!command || head[1]?.quoted || !commands.includes(command as (typeof commands)[number]))
      return fail(`Use: /atomic <${commands.join('|')}>`);
    const atomicCommand = command as (typeof commands)[number];
    const tail = source.slice(head[1].end).trim();
    if (atomicCommand === 'calendar' && tail) {
      if (!/^\d{1,2}$/.test(tail)) return fail('Use: /atomic calendar [1–31 days]');
      const days = Number(tail);
      if (days < 1 || days > 31) return fail('Calendar range must be from 1 to 31 days.');
      return { type: 'atomic.command', command: atomicCommand, days };
    }
    if (tail)
      return fail(`/atomic ${atomicCommand}${atomicCommand === 'calendar' ? ' [1–31 days]' : ''}`);
    return { type: 'atomic.command', command: atomicCommand };
  }
  if (verb === 'cmd') {
    const mode = head[1]?.value.toLowerCase();
    if (!mode || head[1]?.quoted || (mode !== 'post' && mode !== 'brief'))
      return fail('Use: /cmd post <text> or /cmd brief <prompt>');
    const body = source.slice(head[1].end).replace(/^\s+/, '');
    if (mode === 'post') {
      if (!body.trim()) return fail('Atomic post text cannot be empty.');
      if (body.length > 10000) return fail('Atomic post text must be 10,000 characters or fewer.');
      return { type: 'atomic.draft', mode: 'post', text: body };
    }
    return {
      type: 'atomic.draft',
      mode: 'brief',
      text: boundedText(body, 'Atomic brief', 1000),
    };
  }
  if (verb === 'msg') {
    if (head.length !== 2) return fail('Use: msg @person <message> or msg #channel <message>');
    const target = head[1].value;
    const body = boundedText(source.slice(head[1].end), 'Message', 10_000);
    if (target.startsWith('@'))
      return {
        type: 'message.send',
        payload: { body, recipientId: resolvePerson(target, context).id },
      };
    if (target.startsWith('#'))
      return {
        type: 'message.send',
        payload: { body, channelId: resolveChannel(target, context).id },
      };
    return fail('Message destinations start with @person or #channel.');
  }
  let tokens = tokenize(source);
  if (verb === 'c') {
    if (tokens.length === 1) return { type: 'task.new' };
    tokens = [{ ...tokens[0], value: 'task' }, { ...tokens[0], value: 'add' }, ...tokens.slice(1)];
  }
  if (verb === 'repeat') return repeatCommand(tokens, context);
  if (verb === 'notifications') {
    exactArgs(tokens, 1, '/notifications');
    return { type: 'navigate', path: '/settings#notifications' };
  }
  if (verb === 'profile') {
    if (tokens.length === 1) return { type: 'navigate', path: '/profile' };
    exactArgs(tokens, 2, '/profile [@person]');
    return { type: 'navigate', path: `/profiles/${resolvePerson(tokens[1].value, context).id}` };
  }
  if (verb === 'admin') {
    exactArgs(tokens, 1, '/admin');
    return { type: 'navigate', path: '/admin' };
  }
  if (verb === 'crm') {
    exactArgs(tokens, 1, '/crm');
    return { type: 'navigate', path: '/crm' };
  }
  if (verb === 'upcoming' || verb === 'forgotten') {
    const ranges = verb === 'forgotten' ? ['7', '14', '30'] : ['7', '30'];
    let days: string | undefined;
    let projectId: string | undefined;
    for (let i = 1; i < tokens.length; i++) {
      const token = tokens[i];
      if (!token.quoted && token.value === '--project') {
        if (projectId) return fail('--project can only be supplied once.');
        const value = tokens[++i]?.value;
        if (!value) return fail('Provide a project ID after --project.');
        projectId = resolveProject(value, context).id;
      } else if (!days && ranges.includes(token.value)) days = token.value;
      else return fail(`Use: /${verb} [${ranges.join('|')}] [--project <id-or-unique-prefix>]`);
    }
    const query = new URLSearchParams({ days: days || '7' });
    if (projectId) query.set('projectId', projectId);
    return { type: 'navigate', path: `/${verb}?${query}` };
  }
  const routes: Record<string, string> = {
    today: '/today',
    morning: '/today',
    reminders: '/reminders',
    notes: '/notes',
    bookmarks: '/bookmarks',
    tasks: '/tasks',
    projects: '/projects',
  };
  if (Object.hasOwn(routes, verb)) {
    exactArgs(tokens, 1, `/${verb}`);
    return { type: 'navigate', path: routes[verb] };
  }
  if (verb === 'status') {
    if (tokens.length === 2 && !tokens[1].quoted && tokens[1].value === '--ai')
      return {
        type: 'ai.ask',
        scope: 'personal',
        prompt:
          'Summarize my current work and private follow-ups. Highlight what is due and suggest useful next actions for me to review.',
      };
    exactArgs(tokens, 1, '/status');
    return { type: 'agenda.status' };
  }
  if (verb === 'find') {
    if (tokens.length === 1) return { type: 'navigate', path: '/search' };
    return {
      type: 'search',
      query: boundedText(
        tokens
          .slice(1)
          .map((token) => token.value)
          .join(' '),
        'Search',
        200,
      ),
    };
  }
  if (['waiting', 'followup'].includes(verb) && tokens.length === 1)
    return { type: 'navigate', path: '/waiting' };
  if (['remind', 'waiting', 'followup', 'note', 'bookmark'].includes(verb))
    return personalCommand(tokens, context);
  if (['snooze', 'complete', 'resolve'].includes(verb)) {
    exactArgs(
      tokens,
      verb === 'snooze' ? 3 : 2,
      verb === 'snooze' ? '/snooze <id> <30m|2h|1d>' : `/${verb} <id>`,
    );
    const item = resolveItem(boundedText(tokens[1].value, 'Item ID', 100), context);
    if (!['reminder', 'waiting'].includes(item.kind) || item.completedAt)
      return fail('Choose an open reminder or waiting-on item.');
    if (verb === 'resolve' && item.kind !== 'waiting')
      return fail('Choose an open waiting item or follow-up.');
    return {
      type: 'item.update',
      id: item.id,
      patch: {
        expectedVersion: item.version,
        ...(verb !== 'snooze'
          ? { completed: true }
          : {
              scheduledAt: new Date(
                (context.now || new Date()).getTime() + parseDuration(tokens[2].value),
              ).toISOString(),
            }),
      },
    };
  }
  if (verb === 'new-project') {
    exactArgs(tokens, 1, '/new-project');
    return { type: 'navigate', path: '/projects/new' };
  }
  if (verb === 'project') {
    if (
      tokens.length < 2 ||
      tokens.length > 3 ||
      (tokens.length === 3 && (tokens[2].quoted || tokens[2].value !== '--context'))
    )
      return fail('Use: /project <id-or-unique-prefix> [--context]');
    const id = resolveProject(boundedText(tokens[1].value, 'Project ID', 100), context).id;
    return { type: 'navigate', path: `/projects/${id}${tokens.length === 3 ? '?context=1' : ''}` };
  }
  if (verb === 'delete-project') {
    exactArgs(tokens, 2, '/delete-project <id-or-unique-prefix>');
    return {
      type: 'project.delete',
      id: resolveProject(boundedText(tokens[1].value, 'Project ID', 100), context).id,
    };
  }
  if (verb === 'help' || verb === 'clear') {
    exactArgs(tokens, 1, verb);
    return { type: verb };
  }
  if (verb === 'open') {
    exactArgs(tokens, 2, 'open tasks|projects|terminal|shortcuts|settings|#channel|@person');
    const target = tokens[1].value;
    if (target.startsWith('#'))
      return { type: 'navigate', path: `/channels/${resolveChannel(target, context).id}` };
    if (target.startsWith('@'))
      return { type: 'navigate', path: `/people/${resolvePerson(target, context).id}` };
    if (
      [
        'tasks',
        'projects',
        'terminal',
        'shortcuts',
        'settings',
        'today',
        'reminders',
        'waiting',
        'notes',
        'bookmarks',
        'search',
      ].includes(target.toLowerCase())
    )
      return { type: 'navigate', path: `/${target.toLowerCase()}` };
    return fail(
      'Unknown destination. Use tasks, projects, terminal, shortcuts, settings, #channel or @person.',
    );
  }
  if (verb === 'channel') {
    if (tokens[1]?.value.toLowerCase() !== 'add' || tokens.length < 3)
      return fail('Use: channel add <slug> [description]');
    const name = tokens[2].value.replace(/^#/, '');
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(name) || name.length > 40)
      return fail(
        'Channel names use 1–40 lowercase letters, numbers and single separating hyphens.',
      );
    if (context.channels.some((channel) => channel.name === name))
      return fail(`#${name} already exists. Use open #${name}.`);
    const description = tokens
      .slice(3)
      .map((token) => token.value)
      .join(' ')
      .trim();
    if (description.length > 240)
      return fail('Channel description must be 240 characters or fewer.');
    return { type: 'channel.create', payload: { name, ...(description ? { description } : {}) } };
  }
  if (verb !== 'task' && verb !== 'c')
    return fail(`Unknown command "${tokens[0].value}". Run help for available commands.`);
  const operation = tokens[1]?.value.toLowerCase();
  if (operation === 'list') {
    exactArgs(tokens, 2, 'task list');
    return { type: 'task.list' };
  }
  if (['done', 'start', 'reopen', 'delete'].includes(operation)) {
    exactArgs(tokens, 3, `task ${operation} <id-or-unique-prefix>`);
    const task = resolveTask(boundedText(tokens[2].value, 'Task ID', 100), context);
    const id = task.id;
    if (task.recurrence && task.status === 'done')
      return fail('This is completed repeating history. Use the current open occurrence.');
    if (operation === 'delete') return { type: 'task.delete', id };
    return {
      type: 'task.update',
      id,
      patch: {
        status: operation === 'done' ? 'done' : operation === 'start' ? 'in_progress' : 'todo',
        ...(task.version ? { expectedVersion: task.version } : {}),
      },
    };
  }
  if (operation !== 'add')
    return fail('Use: task add <title>, task list, or task done|start|reopen|delete <id>.');
  const payload: NewTask = { title: '' };
  const title: string[] = [];
  const seen = new Set<string>();
  let literal = false;
  for (let i = 2; i < tokens.length; i++) {
    const token = tokens[i];
    if (!literal && !token.quoted && token.value === '--') {
      literal = true;
      continue;
    }
    if (!literal && !token.quoted && token.value.startsWith('--')) {
      const flag = token.value;
      if (!['--priority', '--due', '--assign'].includes(flag))
        return fail(
          `Unknown task option ${flag}. Use --priority, --due or --assign; use -- before a literal title containing flags.`,
        );
      if (seen.has(flag)) return fail(`${flag} can only be supplied once.`);
      seen.add(flag);
      const next = tokens[++i];
      if (!next || (!next.quoted && next.value.startsWith('--')))
        return fail(`Provide a value after ${flag}.`);
      if (flag === '--priority') {
        if (!['low', 'normal', 'high'].includes(next.value))
          return fail('Priority must be low, normal or high.');
        payload.priority = next.value as Task['priority'];
      }
      if (flag === '--due') {
        if (!validDate(next.value))
          return fail('Due date must be a real calendar date in YYYY-MM-DD format.');
        payload.dueDate = next.value;
      }
      if (flag === '--assign') payload.assigneeId = resolvePerson(next.value, context).id;
    } else title.push(token.value);
  }
  payload.title = boundedText(title.join(' '), 'Task title', 240);
  return { type: 'task.create', payload };
}

const roots = [
  ['atomic ', 'Read the configured Atomic workspace'],
  ['c', 'Open the Create task window'],
  ['c ', 'Quick create a task: c <title>'],
  ['cmd ', 'Create an Atomic draft for review; nothing is published'],
  ['ask ', 'Ask the assistant; review actions before applying'],
  ['repeat ', 'Create or stop a repeating task or reminder'],
  ['today', 'Your daily agenda'],
  ['morning', 'Open your daily agenda'],
  ['upcoming ', 'Scheduled work over the next 7 or 30 days'],
  ['forgotten ', 'Review unfinished tasks untouched for 7, 14 or 30 days'],
  ['notifications', 'Configure background alerts and the morning digest'],
  ['profile', 'Edit your personal profile'],
  ['profile ', 'View a teammate’s profile'],
  ['admin', 'Open user management (administrators only)'],
  ['status', 'Private factual status in Terminal'],
  ['remind ', 'Create a private in-app reminder'],
  ['reminders', 'Manage your reminders'],
  ['followup', 'Open your private follow-ups'],
  ['followup ', 'Schedule a private chase'],
  ['resolve ', 'Resolve a waiting item or follow-up'],
  ['waiting', 'View what you are waiting for'],
  ['waiting ', 'Track something you are waiting for'],
  ['snooze ', 'Postpone a reminder or follow-up'],
  ['complete ', 'Complete a reminder or waiting-on item'],
  ['note ', 'Capture a private note'],
  ['notes', 'Open your notes'],
  ['bookmark ', 'Save a private link'],
  ['bookmarks', 'Open your saved links'],
  ['find ', 'Search tasks, projects and your private items'],
  ['tasks', 'Open tasks'],
  ['projects', 'Open projects'],
  ['project ', 'Open a project board and your private context'],
  ['new-project', 'Set up a project and open its Kanban board'],
  ['delete-project ', 'Delete a project after confirmation; keep its tasks'],
  ['help', 'Show command reference'],
  ['clear', 'Clear terminal transcript'],
  ['task add ', 'Create a task'],
  ['task list', 'Show workspace tasks'],
  ['task done ', 'Complete a task'],
  ['task start ', 'Start a task'],
  ['task reopen ', 'Reopen a task'],
  ['task delete ', 'Delete a task after confirmation'],
  ['msg ', 'Message a person or channel'],
  ['open ', 'Open a page or conversation'],
  ['channel add ', 'Create a channel'],
] as const;

export function completeCommand(input: string, context: CommandContext): Completion[] {
  const prefix = input.match(/^\s*(?:\/\s*)?/)?.[0] ?? '';
  const source = input.slice(prefix.length);
  const lower = source.toLowerCase();
  const make = (replacement: string, label: string, detail: string): Completion => ({
    input: prefix + replacement,
    label,
    detail,
  });
  if (/^ask\s/i.test(source)) {
    const tail = source.replace(/^ask\s+/i, '');
    return tail.startsWith('--') && '--shared'.startsWith(tail)
      ? [make('ask --shared ', '--shared', 'Limit context to shared tasks and projects')]
      : [];
  }
  let tokens: Token[];
  try {
    tokens = tokenize(source, true);
  } catch {
    return [];
  }
  const last = tokens.at(-1);
  // Whitespace within an unfinished quote belongs to that token, not a new
  // argument. Completion must never turn a literal title into command options.
  const ended = /\s$/.test(source) && (last?.closed ?? true);
  const verb = tokens[0]?.value.toLowerCase();
  const value = ended ? '' : (last?.value ?? '');
  const replaceFrom = ended ? source.length : (last?.start ?? 0);
  const replacing = source.slice(0, replaceFrom);
  const matches = (candidate: string) => candidate.toLowerCase().startsWith(value.toLowerCase());
  const people = () =>
    context.users
      .filter((user) => matches(`@${user.email}`) || matches(`@${user.name}`))
      .map((user) => make(`${replacing}@${user.email} `, `@${user.name}`, user.email));
  const channels = () =>
    context.channels
      .filter((channel) => matches(`#${channel.name}`))
      .map((channel) =>
        make(
          `${replacing}#${channel.name} `,
          `#${channel.name}`,
          channel.description || 'Workspace channel',
        ),
      );
  if (verb === 'atomic') {
    if (last && !last.closed) return [];
    const position = ended ? tokens.length : tokens.length - 1;
    if (position === 1)
      return ['status', 'calendar', 'approvals', 'content', 'analytics', 'ads', 'decisions']
        .filter(matches)
        .map((command) => make(`${replacing}${command} `, command, 'Atomic read command'));
    if (tokens[1]?.value.toLowerCase() === 'calendar' && position === 2)
      return ['7', '14', '31']
        .filter(matches)
        .map((days) => make(`${replacing}${days} `, days, 'Calendar range in days'));
    return [];
  }
  if (verb === 'cmd') {
    if (last && !last.closed) return [];
    const position = ended ? tokens.length : tokens.length - 1;
    if (position === 1)
      return ['post', 'brief']
        .filter(matches)
        .map((mode) =>
          make(
            `${replacing}${mode} `,
            mode,
            mode === 'post' ? 'Create an exact-text draft' : 'Queue a budgeted Luna draft',
          ),
        );
    return [];
  }
  if (
    verb === 'status' &&
    ((tokens.length === 1 && ended) || (tokens.length === 2 && !ended && !last?.quoted))
  )
    return '--ai'.startsWith(value)
      ? [make('status --ai', '--ai', 'AI summary including your own private items')]
      : [];
  if (verb === 'profile' && ((tokens.length === 1 && ended) || (tokens.length === 2 && !ended))) {
    if (last && !last.closed) return [];
    return people().slice(0, 12);
  }
  if ((verb === 'upcoming' || verb === 'forgotten') && (ended || tokens.length > 1)) {
    if (last && !last.closed) return [];
    const previous = ended ? last : tokens.at(-2);
    if (previous?.value === '--project' && !previous.quoted)
      return context.projects
        .filter(
          (project) =>
            matches(project.id) || project.name.toLowerCase().includes(value.toLowerCase()),
        )
        .slice(0, 12)
        .map((project) =>
          make(`${replacing}${project.id} `, project.name, `Filter ${verb} work to this project`),
        );
    const supplied = ended ? tokens.slice(1) : tokens.slice(1, -1);
    const choices: [string, string][] = [];
    const ranges = verb === 'forgotten' ? ['7', '14', '30'] : ['7', '30'];
    if (!supplied.some((token) => ranges.includes(token.value))) {
      if (verb === 'forgotten')
        ranges.forEach((days) => choices.push([days, `No changes for at least ${days} days`]));
      else choices.push(['7', 'Today and the next six days'], ['30', 'Today and the next 29 days']);
    }
    if (!supplied.some((token) => token.value === '--project'))
      choices.push(['--project', 'Filter by project']);
    return choices
      .filter(([choice]) => matches(choice))
      .map(([choice, detail]) => make(`${replacing}${choice} `, choice, detail));
  }
  if (verb === 'repeat') {
    if (last && !last.closed) return [];
    const position = ended ? tokens.length : tokens.length - 1;
    if (position === 1)
      return ['task', 'reminder', 'stop']
        .filter(matches)
        .map((kind) =>
          make(
            `${replacing}${kind} `,
            kind,
            kind === 'stop'
              ? 'Stop future repeats; keep the current occurrence'
              : `Create a repeating ${kind}`,
          ),
        );
    if (tokens[1]?.value.toLowerCase() === 'stop') {
      if (position === 2)
        return ['task', 'reminder']
          .filter(matches)
          .map((kind) => make(`${replacing}${kind} `, kind, 'Choose the kind of repeating work'));
      if (position === 3) {
        const kind = tokens[2]?.value.toLowerCase();
        const resources =
          kind === 'task' ? context.tasks : kind === 'reminder' ? context.items || [] : [];
        return resources
          .filter(
            (resource) =>
              resource.recurrence?.active &&
              ('status' in resource
                ? resource.status !== 'done'
                : resource.kind === 'reminder' && !resource.completedAt) &&
              (matches(resource.id) || resource.title.toLowerCase().includes(value.toLowerCase())),
          )
          .slice(0, 12)
          .map((resource) =>
            make(
              `${replacing}${resource.id}`,
              resource.title,
              `${kind} · ${resource.id.slice(0, 8)} · stop future repeats`,
            ),
          );
      }
      return [];
    }
    const kind = tokens[1]?.value.toLowerCase();
    if (kind !== 'task' && kind !== 'reminder') return [];
    if (tokens.some((token) => !token.quoted && token.value === '--' && (token !== last || ended)))
      return [];
    const previous = ended ? tokens.at(-1) : tokens.at(-2);
    if (previous && !previous.quoted) {
      if (previous.value === '--every')
        return ['daily', 'weekly', 'monthly']
          .filter(matches)
          .map((frequency) =>
            make(
              `${replacing}${frequency} `,
              frequency,
              'Calendar cadence; completing creates one next occurrence',
            ),
          );
      if (previous.value === '--tz')
        return [
          ...new Set([
            context.timeZone || 'Europe/London',
            'UTC',
            'Europe/London',
            'America/New_York',
          ]),
        ]
          .filter(matches)
          .map((zone) => make(`${replacing}${zone} `, zone, 'Calendar timezone'));
      if (previous.value === '--project')
        return context.projects
          .filter(
            (project) =>
              matches(project.id) || project.name.toLowerCase().includes(value.toLowerCase()),
          )
          .slice(0, 12)
          .map((project) =>
            make(
              `${replacing}${project.id} `,
              project.name,
              'Link the repeating work to this project',
            ),
          );
      if (previous.value === '--assign' && kind === 'task') return people();
      if (previous.value === '--priority' && kind === 'task')
        return ['low', 'normal', 'high']
          .filter(matches)
          .map((priority) => make(`${replacing}${priority} `, priority, 'Task priority'));
      if (['--at', '--from'].includes(previous.value)) return [];
    }
    if ((ended && tokens.length > 2) || (!last?.quoted && value.startsWith('--'))) {
      const flags =
        kind === 'task'
          ? ['--every', '--from', '--tz', '--project', '--priority', '--assign']
          : ['--every', '--at', '--tz', '--project'];
      return flags
        .filter(
          (flag) => matches(flag) && !tokens.some((token) => !token.quoted && token.value === flag),
        )
        .map((flag) =>
          make(
            `${replacing}${flag} `,
            flag,
            flag === '--every'
              ? 'daily, weekly or monthly'
              : flag === '--from'
                ? 'First due date: YYYY-MM-DD'
                : flag === '--at'
                  ? 'First reminder: "tomorrow 10:00"'
                  : flag === '--tz'
                    ? 'IANA timezone'
                    : flag === '--project'
                      ? 'Choose a project'
                      : flag === '--assign'
                        ? 'Choose a teammate'
                        : 'Task priority',
          ),
        );
    }
    return [];
  }
  if (
    ['snooze', 'complete', 'resolve'].includes(verb || '') &&
    ((tokens.length === 1 && ended) || (tokens.length === 2 && !ended))
  ) {
    return (context.items || [])
      .filter(
        (item) =>
          !item.completedAt &&
          (verb === 'resolve'
            ? item.kind === 'waiting'
            : ['reminder', 'waiting'].includes(item.kind)) &&
          (matches(item.id) || item.title.toLowerCase().includes(value.toLowerCase())),
      )
      .slice(0, 12)
      .map((item) =>
        make(
          `${replacing}${item.id}${verb === 'snooze' ? ' ' : ''}`,
          item.title,
          `${item.id.slice(0, 8)} · ${item.kind}`,
        ),
      );
  }
  if (verb === 'snooze' && ((tokens.length === 2 && ended) || (tokens.length === 3 && !ended))) {
    return ['30m', '2h', '1d']
      .filter(matches)
      .map((duration) => make(`${replacing}${duration}`, duration, 'Elapsed time from now'));
  }
  if (['remind', 'waiting', 'followup', 'note', 'bookmark'].includes(verb || '')) {
    if (
      tokens.some((token) => !token.quoted && token.value === '--' && (token !== last || ended)) ||
      (last && !last.closed)
    )
      return [];
    const previous = ended ? tokens.at(-1) : tokens.at(-2);
    if (previous && !previous.quoted && previous.value === '--project') {
      return context.projects
        .filter(
          (project) =>
            matches(project.id) || project.name.toLowerCase().includes(value.toLowerCase()),
        )
        .slice(0, 12)
        .map((project) =>
          make(`${replacing}${project.id} `, project.name, 'Link this private item to the project'),
        );
    }
    if (verb === 'followup' && previous && !previous.quoted && previous.value === '--task') {
      return context.tasks
        .filter(
          (task) => matches(task.id) || task.title.toLowerCase().includes(value.toLowerCase()),
        )
        .slice(0, 12)
        .map((task) =>
          make(`${replacing}${task.id} `, task.title, 'Link this follow-up to a task'),
        );
    }
    if (verb === 'followup' && previous && !previous.quoted && previous.value === '--chat')
      return [...channels(), ...people()].slice(0, 12);
    if (previous && !previous.quoted && previous.value === '--in') {
      return ['30m', '2h', '1d']
        .filter(matches)
        .map((duration) => make(`${replacing}${duration} `, duration, 'Elapsed time from now'));
    }
    if (previous && !previous.quoted && previous.value === '--on') {
      return context.users
        .filter((user) => matches(user.name) || matches(user.email))
        .slice(0, 12)
        .map((user) =>
          make(
            `${replacing}${JSON.stringify(user.name)} `,
            user.name,
            'Private tracking only; no message is sent',
          ),
        );
    }
    if (previous && !previous.quoted && previous.value === '--tz') {
      return [
        ...new Set([
          context.timeZone || 'Europe/London',
          'UTC',
          'Europe/London',
          'America/New_York',
        ]),
      ]
        .filter(matches)
        .map((zone) => make(`${replacing}${zone} `, zone, 'Timezone for the reminder'));
    }
    if (previous && !previous.quoted && previous.value === '--at') return [];
    if ((ended && tokens.length > 1) || (!last?.quoted && value.startsWith('--'))) {
      const flags =
        verb === 'remind'
          ? ['--in', '--at', '--tz', '--project']
          : verb === 'followup'
            ? ['--on', '--in', '--at', '--tz', '--project', '--task', '--chat']
            : verb === 'waiting'
              ? ['--on', '--in', '--at', '--tz', '--project']
              : ['--project'];
      return flags
        .filter(
          (flag) => matches(flag) && !tokens.some((token) => !token.quoted && token.value === flag),
        )
        .map((flag) =>
          make(
            `${replacing}${flag} `,
            flag,
            flag === '--task'
              ? 'Choose a task'
              : flag === '--chat'
                ? 'Choose @person or #channel'
                : flag === '--at'
                  ? '"tomorrow 10:00" or "YYYY-MM-DD HH:mm"'
                  : flag === '--project'
                    ? 'Choose a project'
                    : flag === '--on'
                      ? 'Person or organisation'
                      : flag === '--tz'
                        ? 'IANA timezone'
                        : '30m, 2h or 1d',
          ),
        );
    }
  }
  if (verb === 'project' && ((tokens.length === 2 && ended) || (tokens.length === 3 && !ended))) {
    if (last && !last.closed) return [];
    return '--context'.startsWith(value)
      ? [make(`${replacing}--context`, '--context', 'Show your private project context')]
      : [];
  }
  if (
    (verb === 'delete-project' || verb === 'project') &&
    ((tokens.length === 1 && ended) || (tokens.length === 2 && !ended))
  ) {
    if (last && !last.closed) return [];
    return context.projects
      .filter(
        (project) =>
          matches(project.id) || project.name.toLowerCase().includes(value.toLowerCase()),
      )
      .slice(0, 12)
      .map((project) =>
        make(
          `${replacing}${project.id}`,
          project.name,
          `${project.id.slice(0, 8)} · ${verb === 'project' ? 'Open project board' : 'Delete board, keep tasks'}`,
        ),
      );
  }
  if (
    (verb === 'open' || verb === 'msg') &&
    ((tokens.length === 1 && ended) || (tokens.length === 2 && !ended))
  ) {
    const pages =
      verb === 'open'
        ? [
            'tasks',
            'projects',
            'terminal',
            'shortcuts',
            'settings',
            'today',
            'reminders',
            'waiting',
            'notes',
            'bookmarks',
            'search',
          ]
            .filter(matches)
            .map((page) => make(`${replacing}${page}`, page, `Open ${page}`))
        : [];
    return [...pages, ...channels(), ...people()].slice(0, 12);
  }
  if (
    verb === 'task' &&
    ['done', 'start', 'reopen', 'delete'].includes(tokens[1]?.value.toLowerCase()) &&
    ((tokens.length === 2 && ended) || (tokens.length === 3 && !ended))
  ) {
    return context.tasks
      .filter((task) => matches(task.id))
      .slice(0, 12)
      .map((task) =>
        make(
          `${replacing}${task.id}`,
          `${task.id.slice(0, 8)} · ${task.title}`,
          `${task.status.replace('_', ' ')} · ${task.priority}`,
        ),
      );
  }
  const quickCreate = verb === 'c' && tokens.length > 1;
  if (quickCreate || (verb === 'task' && tokens[1]?.value.toLowerCase() === 'add')) {
    const titleStart = quickCreate ? 1 : 2;
    if (tokens.some((token) => !token.quoted && token.value === '--')) return [];
    const previous = ended ? tokens.at(-1) : tokens.at(-2);
    if (previous && !previous.quoted && previous.value === '--assign') return people().slice(0, 12);
    if (previous && !previous.quoted && previous.value === '--priority')
      return ['low', 'normal', 'high']
        .filter(matches)
        .map((priority) => make(`${replacing}${priority} `, priority, 'Task priority'));
    if (previous && !previous.quoted && previous.value === '--due') return [];
    if ((ended && tokens.length > titleStart) || (!last?.quoted && value.startsWith('--'))) {
      return ['--priority', '--due', '--assign']
        .filter(
          (flag) => matches(flag) && !tokens.some((token) => !token.quoted && token.value === flag),
        )
        .map((flag) =>
          make(
            `${replacing}${flag} `,
            flag,
            flag === '--due'
              ? 'Due date: YYYY-MM-DD'
              : flag === '--assign'
                ? 'Assign to @person'
                : 'low, normal or high',
          ),
        );
    }
  }
  return roots
    .filter(([command]) => command.toLowerCase().startsWith(lower))
    .map(([command, description]) => make(command, command.trim(), description))
    .slice(0, 12);
}
